<template>
    <div
        ref="container"
        class="campaign-banner"
        :class="{
            'campaign-banner--resizing': resizeMode,
            'campaign-banner--dark': isDarkTheme
        }"
    >
        <div v-if="hasHeader" class="campaign-banner__header">
            <slot name="header">
                <div />
            </slot>

            <ScalingHeader
                v-if="showScalingHeader"
                :is-scaled="isScaled"
                :internal-scaling-factor="internalScalingFactor"
                @toggleScaling="toggleScaling"
            />

            <ResizeHeader
                v-if="showResizeHeader"
                :resize-dimensions="resizeDimensions"
                @disableResize="exitResizeMode"
            />
        </div>

        <div v-resize-observer="onContentResize" class="campaign-banner__content">
            <slot name="bannerContainer" />
        </div>

        <div class="campaign-banner__footer">
            <slot name="bannerFooter" />
        </div>
    </div>
</template>

<script lang="ts">
import { vResizeObserver } from "@vueuse/components";

import ScalingHeader from "@/components/Campaign/ScalingHeader.vue";
import ResizeHeader from "@/components/Campaign/ResizeHeader.vue";

const HEADER_HEIGHT = 40;
const DEFAULT_MASTER_TEMPLATE_WIDTH = 240;
const DEFAULT_MASTER_TEMPLATE_HEIGHT = 240;

export default {
    components: {
        ResizeHeader,
        ScalingHeader
    },

    directives: {
        "resize-observer": vResizeObserver
    },

    props: {
        masterTemplateHeight: {
            type: Number,
            default: 240
        },

        masterTemplateWidth: {
            type: Number,
            default: 240
        },

        maxHeight: {
            type: Number
        },

        maxWidth: {
            type: Number
        },

        scalingFactor: {
            type: Number,
            required: true
        },

        resizeMode: {
            type: Boolean,
            default: false
        }
    },

    emits: ["resized", "toggleResizeMode", "scalingToggle", "updateInternalScalingFactor"],

    data() {
        return {
            isFullScale: false,
            resizeDimensions: ""
        };
    },

    computed: {
        mtWidth() {
            return this.masterTemplateWidth || DEFAULT_MASTER_TEMPLATE_WIDTH;
        },

        mtHeight() {
            return this.masterTemplateHeight || DEFAULT_MASTER_TEMPLATE_HEIGHT;
        },

        isDarkTheme() {
            return this.$store.state.ui.themeMode.theme === "dark";
        },

        isScaled() {
            return this.internalScalingFactor !== 1;
        },

        hasHeader() {
            return this.showScalingHeader || this.showResizeHeader || this.$slots.header;
        },

        showScalingHeader() {
            if (this.showResizeHeader) {
                return false;
            }

            if (this.isFullScale) {
                return true;
            }

            return this.isScaled;
        },

        showResizeHeader() {
            return this.resizeMode;
        },

        internalScalingFactor() {
            if (this.resizeMode || this.isFullScale) {
                return 1;
            }

            /* 0 is free scaling 0 - 100% */
            if (this.scalingFactor !== 0) {
                return this.scalingFactor;
            }

            if (!this.maxHeight || !this.maxWidth) {
                return 1;
            }

            const clampedMaxHeight = Math.max(32, this.maxHeight - 130);
            const clampedMaxWidth = Math.max(32, this.maxWidth);

            return Math.min(1, clampedMaxHeight / this.mtHeight, clampedMaxWidth / this.mtWidth);
        },

        contentHeight() {
            return Math.round(this.mtHeight * this.internalScalingFactor);
        },

        contentWidth() {
            return Math.round(this.mtWidth * this.internalScalingFactor);
        }
    },

    watch: {
        isFullScale() {
            this.resetContainerStyle();
        },

        resizeMode() {
            this.resetContainerStyle();
        },

        internalScalingFactor: {
            handler() {
                this.resetContainerStyle();

                this.$emit("updateInternalScalingFactor", this.internalScalingFactor);
            },
            immediate: true
        }
    },

    mounted() {
        this.resetContainerStyle();
    },

    methods: {
        resetContainerStyle() {
            if (!this.$refs.container) {
                return;
            }

            const padding = this.hasHeader ? 2 * HEADER_HEIGHT : HEADER_HEIGHT;

            (this.$refs.container as HTMLDivElement).style.width = `${this.contentWidth}px`;
            (this.$refs.container as HTMLDivElement).style.height = `${this.contentHeight + padding}px`;
        },

        onContentResize(entries: ResizeObserverEntry[]) {
            const { contentRect } = entries[0];

            const width = Math.round(contentRect.width);
            const height = Math.round(contentRect.height);

            this.resizeDimensions = `(${width} x ${height})`;

            this.$emit("resized", { width, height });
        },

        toggleScaling() {
            this.isFullScale = !this.isFullScale;
            this.$emit("scalingToggle", !this.isFullScale);
        },

        exitResizeMode() {
            this.$emit("toggleResizeMode", false);
        }
    }
};
</script>

<style lang="scss">
@use "@style/variables" as *;
$secondaryBackColor: #1b1f28;

.campaign-banner {
    overflow: hidden;

    border: 1px solid $cmp-light-border-color;

    display: flex;
    flex-direction: column;

    box-sizing: content-box;
}

.campaign-banner__header,
.campaign-banner__footer {
    color: $cmp-light-font-color;
    flex: 0 0 40px;
}

.campaign-banner--dark {
    border-color: $darktheme40;

    .campaign-banner__header,
    .campaign-banner__footer {
        background-color: $secondaryBackColor;
    }
}

.campaign-banner--resizing {
    resize: both;
}

.campaign-banner__header {
    width: 100%;
    align-items: center;
    justify-content: space-between;
    background: $white;
    color: $darktheme20;
    display: flex;
    font-size: $font-size-small;
}

.campaign-banner__content {
    flex: 1 1 auto;

    display: flex;

    position: relative; // for the qa preview annotation layer
}

.campaign-banner__checkbox {
    width: 100%;
    height: 100%;

    position: relative;
    margin-right: 0;

    .ivu-checkbox {
        display: flex;

        position: absolute;
        top: 10px;
        left: 10px;
        font-size: 20px;
        z-index: 1;

        &-inner {
            width: 20px;
            height: 20px;

            &:after {
                height: 14px;
                left: 8px;
            }
        }
    }

    .ivu-checkbox-label-text {
        margin: 0;
        margin-right: 0;
        padding: 0;
        display: flex;

        height: 100%;
    }
}

$height: 40px;
$button-width: 40px;

.campaign-banner__footer {
    width: 100%;
    height: $height;

    display: flex;
    justify-content: flex-start;
    align-items: center;
    text-align: center;
}
</style>
